import React from "react";
import { motion } from "framer-motion";
import { Users, FileSignature, Wallet, ShieldCheck, ArrowUpRight } from "lucide-react";

/* ─── Animated sparkline with path-draw ─── */
const AnimatedSparkline = () => {
  const data = [20, 28, 22, 34, 30, 42, 38, 52, 46, 60, 55, 72];
  const max = Math.max(...data);
  const W = 100, H = 52;
  const pts = data.map((v, i) => ({
    x: (i / (data.length - 1)) * W,
    y: H - (v / max) * (H * 0.9),
  }));
  const linePath = pts.reduce((acc, p, i) => {
    if (i === 0) return `M ${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    const prev = pts[i - 1];
    const cx = ((prev.x + p.x) / 2).toFixed(1);
    return `${acc} C ${cx},${prev.y.toFixed(1)} ${cx},${p.y.toFixed(1)} ${p.x.toFixed(1)},${p.y.toFixed(1)}`;
  }, "");
  const fillPath = `${linePath} L ${W},${H} L 0,${H} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-12">
      <defs>
        <linearGradient id="sg1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF4F00" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#FF4F00" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill="url(#sg1)" />
      <motion.path
        d={linePath}
        fill="none"
        stroke="#FF4F00"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.4, ease: "easeInOut", delay: 0.1 }}
      />
      <motion.circle
        cx={pts[pts.length - 1].x}
        cy={pts[pts.length - 1].y}
        r="2"
        fill="#FF4F00"
        initial={{ scale: 0 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.3, delay: 1.4 }}
      />
    </svg>
  );
};

/* ─── Compliance mini ring (right panel) ─── */
const ComplianceRing = ({ score }) => {
  const r = 14, circ = 2 * Math.PI * r;
  return (
    <svg width="36" height="36" viewBox="0 0 36 36">
      <circle cx="18" cy="18" r={r} stroke="#F0F0F0" strokeWidth="4" fill="none" />
      <motion.circle
        cx="18" cy="18" r={r}
        stroke="#FF4F00"
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={circ}
        initial={{ strokeDashoffset: circ }}
        whileInView={{ strokeDashoffset: circ - (score / 100) * circ }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: "easeOut", delay: 0.6 }}
        transform="rotate(-90 18 18)"
      />
      <text x="18" y="22" textAnchor="middle" fontSize="8" fill="#0A0A0B" fontWeight="700">{score}</text>
    </svg>
  );
};

/* ─── Admin Dashboard — 3-panel with staggered animations ─── */
export const AdminDashboard = ({ compact = false }) => {
  const kpis = [
    { label: "Active contractors", value: "92", delta: "+4 this week", icon: <Users className="w-3 h-3" />, tone: "green" },
    { label: "Expiring · 30d", value: "5", delta: "contracts due", icon: <FileSignature className="w-3 h-3" />, tone: "amber" },
    { label: "Invoices pending", value: "14", delta: "needs attention", icon: <Wallet className="w-3 h-3" />, tone: "accent" },
  ];
  const activity = [
    { name: "Lena Müller", role: "Contractor onboarded", time: "2m" },
    { name: "Atlas Studio", role: "Contract signed · DE-2041", time: "11m" },
    { name: "Yusuf Okafor", role: "Timesheet approved", time: "34m" },
    { name: "Mira Hahn", role: "Payout queued · €1,240", time: "1h" },
  ];
  const online = [
    { initials: "LM", name: "Lena M.", project: "Atlas brand" },
    { initials: "YO", name: "Yusuf O.", project: "Data migration" },
    { initials: "SK", name: "Sasha K.", project: "Audit prep" },
  ];

  return (
    <div
      data-testid="admin-dashboard-mockup"
      className="relative rounded-2xl overflow-hidden border border-[#E5E5E7] bg-[#FAFAFA] text-[#0A0A0B] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.5)]"
    >
      {/* Window chrome */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E5E5E7] bg-white">
        <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
        <div className="ml-3 px-2.5 py-1 rounded-md bg-[#F4F4F5] text-[10px] text-[#71717A] font-mono flex items-center gap-1.5">
          <ShieldCheck className="w-2.5 h-2.5" />
          app.nexus-vs.vawick.com
        </div>
        <div className="ml-auto flex items-center gap-2.5">
          <span className="text-[9px] font-mono text-[#A1A1AA]">⌘K</span>
          <div className="w-5 h-5 rounded-full bg-[#FF4F00] text-white text-[9px] grid place-items-center font-bold">N</div>
        </div>
      </div>

      {/* 3-panel body */}
      <div className="grid grid-cols-12 divide-x divide-[#F0F0F0]">
        {/* Left: Nav sidebar */}
        <aside className="col-span-3 bg-white py-4 px-3 hidden sm:flex flex-col">
          <div className="flex items-center gap-2 px-2 mb-5">
            <div className="w-5 h-5 rounded bg-[#FF4F00]/10 border border-[#FF4F00]/30 grid place-items-center">
              <div className="w-1.5 h-1.5 rounded-full bg-[#FF4F00]" />
            </div>
            <span className="text-[11px] font-display font-semibold tracking-tight">Acme Ops</span>
          </div>
          <div className="text-[8px] uppercase tracking-[0.22em] text-[#A1A1AA] px-2 mb-2">Workspace</div>
          {["Dashboard", "Team", "Time Tracking", "Contracts", "eSignature", "Payouts", "Compliance"].map((label, i) => (
            <div
              key={label}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-[11px] mb-0.5 ${
                i === 0 ? "bg-[#FFF3EE] text-[#FF4F00] font-semibold" : "text-[#52525B]"
              }`}
            >
              <span className={`w-1 h-1 rounded-full ${i === 0 ? "bg-[#FF4F00]" : "bg-transparent"}`} />
              {label}
            </div>
          ))}
          <div className="mt-auto pt-3 px-2 flex items-center gap-2 border-t border-[#F4F4F5]">
            <div className="w-5 h-5 rounded-full bg-[#F1F1F4] text-[8px] text-[#52525B] grid place-items-center font-bold shrink-0">JD</div>
            <div className="min-w-0">
              <div className="text-[10px] font-medium leading-none">Jay Durand</div>
              <div className="text-[9px] text-[#A1A1AA] mt-0.5">Admin</div>
            </div>
          </div>
        </aside>

        {/* Center: Main content */}
        <main className="col-span-12 sm:col-span-6 p-4 bg-[#FAFAFA]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-0.5">Operations · Q4 W49</div>
              <div className="font-display text-[15px] font-semibold tracking-tight">Today&apos;s overview</div>
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#F4F4F5] text-[9px] text-[#52525B] font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-[#28C840] pulse-dot" />
              live
            </div>
          </div>

          {/* KPI tiles — staggered entrance */}
          <motion.div
            className="grid grid-cols-3 gap-2 mb-3"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
          >
            {kpis.map((k) => (
              <motion.div
                key={k.label}
                variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.4 }}
                className="rounded-xl border border-[#E5E5E7] bg-white p-2.5"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[9px] uppercase tracking-wider text-[#A1A1AA]">{k.label}</span>
                  <span className={k.tone === "amber" ? "text-[#D97706]" : k.tone === "green" ? "text-[#059669]" : k.tone === "accent" ? "text-[#FF4F00]" : "text-[#A1A1AA]"}>{k.icon}</span>
                </div>
                <div className={`font-display text-xl font-semibold tracking-tight ${k.tone === "amber" ? "text-[#D97706]" : k.tone === "green" ? "text-[#059669]" : k.tone === "accent" ? "text-[#FF4F00]" : "text-[#0A0A0B]"}`}>{k.value}</div>
                <div className="text-[9px] font-mono text-[#A1A1AA] mt-0.5">{k.delta}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* Animated sparkline chart */}
          <div className="rounded-xl border border-[#E5E5E7] bg-white p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] text-[#52525B]">Payout volume · 12 weeks</span>
              <span className="text-[10px] font-mono text-[#059669] font-semibold">▲ 18.4%</span>
            </div>
            <AnimatedSparkline />
          </div>

          {/* Activity feed — staggered slide-in */}
          {!compact && (
            <motion.div
              className="rounded-xl border border-[#E5E5E7] bg-white overflow-hidden"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07, delayChildren: 0.3 } } }}
            >
              {activity.map((row, i) => (
                <motion.div
                  key={i}
                  variants={{ hidden: { opacity: 0, x: -6 }, show: { opacity: 1, x: 0 } }}
                  transition={{ duration: 0.3 }}
                  className="flex items-center gap-3 px-3 py-2 border-b border-[#F4F4F5] last:border-0"
                >
                  <div className="w-6 h-6 rounded-full bg-[#F1F1F4] grid place-items-center text-[9px] text-[#52525B] font-medium shrink-0">
                    {row.name.split(" ").map(p => p[0]).join("")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-medium truncate">{row.name}</div>
                    <div className="text-[9px] text-[#71717A] truncate">{row.role}</div>
                  </div>
                  <div className="text-[9px] font-mono text-[#A1A1AA] shrink-0">{row.time}</div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </main>

        {/* Right: Detail panel */}
        <aside className="col-span-3 bg-white p-3 hidden sm:block">
          <div className="flex items-center gap-1.5 mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#28C840] pulse-dot" />
            <span className="text-[8px] uppercase tracking-[0.22em] text-[#A1A1AA]">Online now</span>
          </div>
          <motion.div
            className="space-y-1.5 mb-4"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1, delayChildren: 0.5 } } }}
          >
            {online.map((c) => (
              <motion.div
                key={c.name}
                variants={{ hidden: { opacity: 0, x: 8 }, show: { opacity: 1, x: 0 } }}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-[#F4F4F5] bg-[#FAFAFA]"
              >
                <div className="relative shrink-0">
                  <div className="w-5 h-5 rounded-full bg-[#E5E5E7] text-[8px] text-[#52525B] grid place-items-center font-semibold">{c.initials}</div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[#28C840] border border-white" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-medium truncate">{c.name}</div>
                  <div className="text-[9px] text-[#A1A1AA] truncate">{c.project}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="rounded-lg border border-[#E5E5E7] bg-[#FAFAFA] p-2.5 mb-3">
            <div className="text-[8px] uppercase tracking-[0.22em] text-[#A1A1AA] mb-2">Compliance</div>
            <div className="flex items-center gap-2.5">
              <ComplianceRing score={96} />
              <div>
                <div className="text-[12px] font-display font-semibold leading-none">96/100</div>
                <div className="text-[9px] text-[#059669] font-mono mt-0.5">healthy</div>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <button className="w-full py-1.5 px-2.5 rounded-lg border border-[#FF4F00]/25 bg-[#FF4F00]/5 text-[10px] text-[#FF4F00] font-semibold text-left">
              Review approvals →
            </button>
            <button className="w-full py-1.5 px-2.5 rounded-lg border border-[#E5E5E7] text-[10px] text-[#52525B] text-left">
              Run payroll →
            </button>
            <button className="w-full py-1.5 px-2.5 rounded-lg border border-[#E5E5E7] text-[10px] text-[#52525B] text-left">
              New contract →
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

/* ─── Manager Dashboard ─── */
export const ManagerDashboard = () => {
  const projects = [
    { name: "Atlas / brand work", pct: 78 },
    { name: "Helio / data migration", pct: 42 },
    { name: "Mercer / audit", pct: 91 },
  ];
  return (
    <div className="relative rounded-2xl overflow-hidden border border-[#122020] bg-[#0A1212] text-[#F2F6F6]">
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#7A9B9B] mb-0.5">Manager view</div>
            <div className="font-display text-[15px] font-semibold tracking-tight">Team — Operations North</div>
          </div>
          <div className="text-[10px] font-mono text-[#7A9B9B]">8 members</div>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            { l: "On track", v: "2/3", t: "green" },
            { l: "Approvals", v: "6", t: "amber" },
            { l: "Hours", v: "312h", t: "default" },
          ].map((c) => (
            <div key={c.l} className="rounded-xl border border-[#122020] bg-[#060C0C] p-2.5">
              <div className="text-[9px] uppercase tracking-wider text-[#7A9B9B] mb-1.5">{c.l}</div>
              <div className={`font-display text-xl font-semibold ${c.t === "green" ? "text-[#34D399]" : c.t === "amber" ? "text-[#FBBF24]" : "text-[#F2F6F6]"}`}>{c.v}</div>
            </div>
          ))}
        </div>
        <motion.div
          className="rounded-xl border border-[#101C1C] bg-[#080F0F] overflow-hidden"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
        >
          {projects.map((p) => (
            <motion.div
              key={p.name}
              variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}
              className="px-3 py-2.5 border-b border-[#0E1A1A] last:border-0"
            >
              <div className="flex justify-between text-[11px] mb-1.5">
                <span className="text-[#F2F6F6]">{p.name}</span>
                <span className="font-mono text-[#9BB8B8]">{p.pct}%</span>
              </div>
              <div className="h-1 rounded-full bg-[#0F1E1E] overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-[#FF4F00]"
                  initial={{ width: 0 }}
                  whileInView={{ width: `${p.pct}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

/* ─── Contractor Dashboard ─── */
export const ContractorDashboard = () => (
  <div className="relative rounded-2xl overflow-hidden border border-[#E5E5E7] bg-white text-[#0A0A0B] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)]">
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#F0F0F0] bg-[#FAFAFA]">
      <span className="w-2 h-2 rounded-full bg-[#FF5F57]" />
      <span className="w-2 h-2 rounded-full bg-[#FEBC2E]" />
      <span className="w-2 h-2 rounded-full bg-[#28C840]" />
      <div className="ml-3 text-[9px] font-mono text-[#A1A1AA]">app.nexus-vs.io / my-workspace</div>
    </div>
    <div className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-0.5">Contractor</div>
          <div className="font-display text-[15px] font-semibold tracking-tight">Welcome back, Mira</div>
        </div>
        <div className="text-[10px] font-mono text-[#71717A]">€4,820 ytd</div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-xl border border-[#EAEAEC] bg-[#FAFAFA] p-3">
          <div className="text-[9px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">Live timer</div>
          <div className="flex items-end gap-1">
            <span className="font-mono text-2xl font-semibold">02:14</span>
            <span className="font-mono text-xs text-[#A1A1AA] mb-0.5">:38</span>
          </div>
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#059669] pulse-dot" />
            <span className="text-[9px] text-[#059669]">Atlas brand</span>
          </div>
        </div>
        <div className="rounded-xl border border-[#EAEAEC] bg-[#FAFAFA] p-3">
          <div className="text-[9px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">Next payout</div>
          <div className="font-display text-2xl font-semibold tracking-tight">€1,240</div>
          <div className="text-[9px] text-[#71717A] mt-1">31 Dec · SEPA</div>
        </div>
      </div>
      <div className="rounded-xl border border-[#EAEAEC] bg-[#FAFAFA] p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-semibold">Onboarding</span>
          <span className="text-[9px] font-mono text-[#059669]">4 / 5 done</span>
        </div>
        <div className="flex gap-1 mb-2">
          {[1, 2, 3, 4, 5].map((s) => (
            <motion.div
              key={s}
              className={`flex-1 h-1 rounded-full ${s <= 4 ? "bg-[#FF4F00]" : "bg-[#E5E5E7]"}`}
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: s * 0.08 }}
              style={{ originX: 0 }}
            />
          ))}
        </div>
        <div className="text-[9px] text-[#71717A]">Sign contract · DE-2041 to complete</div>
      </div>
    </div>
  </div>
);

// Floating stat card for hero
export const FloatingStatCard = ({ icon, label, value, tone = "default", testId, className = "" }) => {
  const toneColor = {
    default: "#FAFAFA",
    green: "#34D399",
    amber: "#FBBF24",
    accent: "#FF4F00",
  }[tone];
  return (
    <motion.div
      data-testid={testId}
      whileHover={{ y: -4 }}
      className={`glass rounded-2xl px-4 py-3 min-w-[200px] ${className}`}
    >
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: `${toneColor}22`, color: toneColor }}>
          {icon}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#9BB8B8]">{label}</div>
          <div className="font-display text-base font-medium tracking-tight text-white">{value}</div>
        </div>
        <ArrowUpRight className="w-3.5 h-3.5 text-[#7A9B9B] ml-auto" />
      </div>
    </motion.div>
  );
};
